import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';
import { Role } from 'constants/roles';

import React, { useEffect } from 'react';

import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { useLocalDatagrid } from 'hooks/useLocalDatagrid';
import { useLocalDatagridPagination } from 'hooks/useLocalDatagridPagination';
import { usePermissions } from 'hooks/usePermissions';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import {
  NumberField,
  RaRecord,
  RecordContextProvider,
  useListContext,
  useRemoveFromStore,
} from 'react-admin';
import { Chip } from 'shared/mui/Chips';
import { ContextMenu, ContextMenuShowButton } from 'shared/mui/ContextMenu';
import { TablePagination } from 'shared/mui/Table';
import { DatagridStickyContainer } from 'shared/react-admin/Datagrids/DatagridSticky/DatagridStickyContainer';
import {
  ChipField,
  CopyToClipboardLinkField,
  DateTimeField,
  EmbeddedFunctionField,
  FunctionField,
  OrderStatusChipField,
  TextField,
} from 'shared/react-admin/Fields';
import { CopyToClipboardField } from 'shared/react-admin/Fields/CopyToClipboardField/CopyToClipboardField';
import { OrderList } from 'types/embedded/order';
import { getId } from 'utils';

import { DatagridConfigurableWithStickyActionsStyled } from './OrdersListLocalDatagrid.styled';
import { OrdersListBulkActions } from '../OrdersListBulkActions';
import { OrdersListLocalDatagridCopyButton } from './OrdersListLocalDatagridCopyButton';
import { getRowStyle } from './utils/getRowStyle';

type Props = {
  orders: OrderList[];
  total: number;
  isLoading: boolean;
  timezone: string;
};

export const OrdersListLocalDatagrid: React.FC<Props> = ({
  total,
  isLoading,
  orders = [],
  timezone,
}) => {
  const { createPathWithQuery } = useCreatePathWithQuery();
  const translate = useTypedTranslate();
  const { hasResourceAccess, isHaveRole } = usePermissions();
  const canShowBulkActions =
    hasResourceAccess(Resources.OrderAdjustments) || isHaveRole([Role.Admin, Role.Payment]);
  const { resource } = useListContext();
  const remove = useRemoveFromStore();

  const { sortedData, sort, setSort } = useLocalDatagrid({
    data: orders,
    defaultSort: { field: 'createdAt', order: 'ASC' },
  });

  const { dataPerPage, ...paginationProps } = useLocalDatagridPagination<OrderList>({
    data: sortedData,
    defaultPage: 0,
    defaultRowsPerPage: 10,
  });

  useEffect(() => {
    return () => {
      remove(`${resource}.selectedIds`);
    };
  }, []);

  return (
    <>
      <DatagridStickyContainer>
        <DatagridConfigurableWithStickyActionsStyled
          bulkActionButtons={canShowBulkActions && <OrdersListBulkActions />}
          data={dataPerPage}
          isLoading={isLoading}
          rowSx={getRowStyle}
          setSort={setSort}
          sort={sort}
          sx={{
            // Переопределение глобальных стилей, которые ломают sticky у заголовка
            overflowX: 'visible',
            overflowY: 'visible',
            '& > div:has(> .MuiToolbar-root)': {
              position: 'static !important',
              top: 'auto !important',
              left: 'auto !important',
              zIndex: 'auto !important',
            },
          }}
        >
          <CopyToClipboardLinkField
            getLink={(record: RaRecord) =>
              createPathWithQuery({ resource: Resources.Orders, type: 'show', id: record['@id'] })
            }
            getValue={getId}
            isTrimValue
            label={translate('app.table.idKubera')}
            source="@id"
            target="_blank"
          />
          <CopyToClipboardField
            isTrimValue
            label={translate('app.table.idMerchant')}
            source="merchantReference"
          />
          <CopyToClipboardField
            getValue={getId}
            isTrimValue
            label={translate('app.table.idProvider')}
            source="providerReference"
          />
          <DateTimeField
            label={translate('app.table.creationTime')}
            source="createdAt"
            timezone={timezone}
          />
          <DateTimeField
            label={translate('app.table.updateTime')}
            source="updatedAt"
            timezone={timezone}
          />
          <NumberField
            label={translate('app.table.amount')}
            options={{ minimumFractionDigits: 2 }}
            source="amount"
          />
          {hasResourceAccess(Resources.Currencies) && (
            <EmbeddedFunctionField
              cellClassName="column-currency"
              label={translate('app.table.currency')}
              source="currency"
              sourceField="currency"
              sx={{ color: 'primary.main', fontWeight: 700 }}
            />
          )}
          <FunctionField
            cellClassName="column-direction"
            label={translate('app.table.direction')}
            render={(record: OrderList) => (
              <RecordContextProvider value={{ direction: record.direction }}>
                <ChipField source="direction" />
              </RecordContextProvider>
            )}
            source="direction"
          />
          <FunctionField
            cellClassName="column-method"
            label={translate('app.table.method')}
            render={(record: OrderList) => record.method}
            source="method"
          />
          <FunctionField
            cellClassName="column-status"
            label={translate('app.table.status')}
            render={(record: OrderList) => (
              <RecordContextProvider value={{ status: record.status, value: record.statusAlias }}>
                <OrderStatusChipField source="status" />
              </RecordContextProvider>
            )}
            sortable={false}
            source="status"
          />
          {hasResourceAccess('merchants') && (
            <EmbeddedFunctionField
              cellClassName="column-merchant"
              label={translate('app.table.merchant')}
              render={(value) => <Chip label={value} />}
              source="merchant"
              sourceField="merchant"
            />
          )}
          <CopyToClipboardField
            isTrimValue
            label={translate('app.table.client')}
            source="customerId"
            underline={false}
          />
          <CopyToClipboardField
            label={translate('app.table.clientData')}
            source="customerData"
            underline={false}
          />
          {hasResourceAccess(Resources.Providers) && (
            <EmbeddedFunctionField
              cellClassName="column-provider"
              label={translate('app.table.provider')}
              sortBy="provider"
              source="provider"
              sourceField="provider"
            />
          )}
          {hasResourceAccess(Resources.Gateways) && (
            <EmbeddedFunctionField
              cellClassName="column-gateway"
              label={translate('app.table.gateway')}
              sortable={false}
              source="gateway"
              sourceField="gateway"
            />
          )}
          <TextField
            emptyText={EMPTY_FIELD}
            label={translate('app.table.declineReason')}
            source="providerExternalError"
          />
          <ContextMenu>
            <OrdersListLocalDatagridCopyButton timezone={timezone} />
            <ContextMenuShowButton linkProps={{ target: '_blank', resource: Resources.Orders }} />
          </ContextMenu>
        </DatagridConfigurableWithStickyActionsStyled>
      </DatagridStickyContainer>

      {total > 0 && (
        <TablePagination count={total} {...paginationProps} sx={{ display: 'block' }} />
      )}
    </>
  );
};
